import { CustomerContext, CustomerType } from '@typesApp/Customer'
import { HostCustomerContext, HostEnvironment } from '@typesApp/Environment'
import { AppConfig, SanitizedAppConfig } from '@typesApp/Widget'
import { ROUTES } from '@constants/index'
import { normalizeCartId } from '@utils/customerContextUtil'
import { isCustomerNumberValid, normalizeCustomerNumber } from '@utils/isCustomerNumberValid'
import { normalizeBrandFromHost } from '@utils/normalizeBrand'

/**
 * To support local development with specific brand mappings
 */
const LOCAL_BRAND_OVERRIDE = {
  cle: 'cle-ca',
  vde: 'vde-fr',
  nua: 'nua-us',
} as const

/**
 * Commerce app may use a specific user id for the anonymous user.
 * e.g. N1 sends -1002, but we allow for commerce app to override
 * this value via the session.anonymousUserId configuration.
 */
const DEFAULT_ANONYMOUS_USER_ID = '-1002'

const DEFAULT_ENVIRONMENT: HostEnvironment = {
  host: '',
  brand: '',
  country: '',
  locale: '',
  lang: '',
  currency: '',
  dateFormat: 'MM/dd/yyyy',
  basePath: '/',
  cartPath: '',
  checkoutPath: '',
  signInPath: '',
  signInRedirectParam: '',
  signInRedirectStripLocale: false,
  ecommerceCode: '',
  termsConditionsUrl: '',
  privacyPolicyUrl: '',
  cookiesPolicyUrl: '',
  insuranceLandingPageUrl: '',
  faqUrl: '',
  anonymousUserId: DEFAULT_ANONYMOUS_USER_ID,
}

/**
 * Sanitize configuration fields to ensure downstream components receive
 * the configuration objects in the expected shape. Sanitization includes:
 * - handling of deprecated fields
 * - handling of brand specific quirks
 * - normalization of fields, e.g. numbers converted to strings, trimming, etc
 */
export const sanitizeAppConfig = (config: AppConfig): SanitizedAppConfig => {
  const {
    selector,
    session,
    customerContext: hostCustomerContext,
    environment: hostEnvironment,
    actionModule,
    ...rest
  } = config ?? {}

  const environment = normalizeEnvironment(hostEnvironment)

  // NOTE: use session for backwards compatibility until all integrations update to use customerContext
  const customerContextFromCommerce = hostCustomerContext ?? session
  const customerContext = getNormalizedCustomerContext(customerContextFromCommerce, environment.anonymousUserId)

  return {
    selector,
    environment,
    actionModule,
    customerContext,
    ...rest,
  }
}

const normalizeEnvironment = (environment: HostEnvironment | undefined): HostEnvironment => {
  if (!environment) return DEFAULT_ENVIRONMENT

  const copy = {
    ...environment,
    brand: getBrandId(environment.host, environment.brand),
    basePath: getBasePath(),
    basePathSearch: window.location.search ?? '',
    anonymousUserId: `${environment.anonymousUserId ?? DEFAULT_ANONYMOUS_USER_ID}`,
    locale: environment.locale?.toLowerCase() ?? '',
    lang: environment.locale?.toLowerCase()?.split('-')[0] ?? 'en',
  }

  if (typeof environment?.channel === 'string') {
    copy.channel = environment.channel.trim().toUpperCase()
  } else {
    delete copy.channel
  }

  return copy
}

const getBrandId = (host: string, brandFallback: string): string => {
  const isLocal = host.includes('localhost') || host.includes('local.')
  if (isLocal) {
    return LOCAL_BRAND_OVERRIDE[brandFallback] ?? brandFallback
  }
  return normalizeBrandFromHost(host) ?? brandFallback
}

const getBasePath = (): string => {
  const routeBase = ROUTES.BASE.replace('/', '')

  let basePath = window.location.pathname
  const pathArray = basePath.split('/')
  if (pathArray.includes(routeBase)) {
    const dbIndex = pathArray.indexOf(routeBase)
    basePath = pathArray.slice(0, dbIndex).join('/')
  }
  return basePath
}

const getNormalizedCustomerContext = (
  context: HostCustomerContext | undefined,
  anonymousUserId: string | undefined
): CustomerContext => {
  // remove all deprecated fields and normalize values

  const result: CustomerContext = {
    customerType: getCustomerType(context, anonymousUserId),
  }

  if (result.customerType === 'ANONYMOUS') {
    return result
  }

  result.userId = normalizeCustomerNumber(context?.userId)
  result.cartId = normalizeCartId(context?.cartId)
  return result
}

const getCustomerType = (
  context: HostCustomerContext | undefined,
  anonymousUserId: string | undefined
): CustomerType => {
  const { isGuest, isLoggedIn, isPartiallyAuthenticated, customerType, userId } = context ?? {}

  // customer type beats all
  const trimmedCustomerType = typeof customerType === 'string' ? customerType.trim().toUpperCase() : undefined
  if (trimmedCustomerType === 'ANONYMOUS' || trimmedCustomerType === 'GUEST' || trimmedCustomerType === 'REGISTERED') {
    return trimmedCustomerType
  }

  // anonymousUserId check
  const normalizedUserId = normalizeCustomerNumber(userId)
  if (normalizedUserId && !isCustomerNumberValid(normalizedUserId, anonymousUserId)) return 'ANONYMOUS'

  // fallback to boolean flags
  if (isLoggedIn || isPartiallyAuthenticated) return 'REGISTERED'
  return isGuest ? 'GUEST' : 'ANONYMOUS'
}
